import { Button, notify, Switch } from '@affine/component';
import {
  SettingsCollapsible,
  SettingsRow,
  SettingsSubRows,
} from '@affine/component/setting-components';
import { SettingsTypedConfirmButton } from '@affine/core/desktop/dialogs/setting/shared/ui/settings-typed-confirm';
import { EnableCloudPanel } from '@affine/core/desktop/dialogs/setting/workspace-setting/preference/enable-cloud';
import { WorkspaceDialogService } from '@affine/core/modules/dialogs';
import { UserFriendlyError } from '@affine/error';
import { ServerFeature } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import track from '@affine/track';
import { useLiveData, useService } from '@toeverything/infra';
import type { ReactNode } from 'react';
import { useCallback, useEffect } from 'react';

import { ServerService } from '../../cloud/services/server';
import { WorkspaceService } from '../../workspace/services/workspace';
import { EmbeddingService } from '../services/embedding';
import { EmbeddingStore } from '../stores/embedding';
import { EmbeddingProgress } from './embedding-progress';
import { IgnoredDocs } from './ignored-docs';

const REINDEX_WORD = 'RE-INDEX';

interface EmbeddingSettingsProps {
  /** Further rows of the "Advanced" part, e.g. how scanned PDFs are read. */
  advancedExtra?: ReactNode;
}

const EmbeddingCloud = ({ advancedExtra }: EmbeddingSettingsProps) => {
  const t = useI18n();
  const embeddingService = useService(EmbeddingService);
  const embeddingStore = useService(EmbeddingStore);
  const workspaceId = useService(WorkspaceService).workspace.id;
  const workspaceDialogService = useService(WorkspaceDialogService);

  const embeddingEnabled = useLiveData(
    embeddingService.embeddingEnabled.enabled$
  );
  const ignoredDocs = useLiveData(embeddingService.ignoredDocs.docs$);
  const embeddingProgress = useLiveData(
    embeddingService.embeddingProgress.progress$
  );
  const isIgnoredDocsLoading = useLiveData(
    embeddingService.ignoredDocs.loading$
  );
  const isEnabledLoading = useLiveData(
    embeddingService.embeddingEnabled.loading$
  );

  const handleEmbeddingToggle = useCallback(
    (checked: boolean) => {
      track.$.settingsPanel.indexerEmbedding.toggleWorkspaceEmbedding({
        type: 'Embedding',
        control: 'Workspace embedding',
        option: checked ? 'on' : 'off',
      });

      embeddingService.embeddingEnabled
        .setEnabled(checked)
        .then(() => {
          if (checked) {
            embeddingService.embeddingProgress.startEmbeddingProgressPolling();
          }
        })
        .catch((error: unknown) => {
          const err = UserFriendlyError.fromAny(error);
          notify.error({
            title:
              t[
                'com.streamlain.settings.workspace.indexer-embedding.embedding.switch.error'
              ](),
            message: t[`error.${err.name}`](err.data),
          });
        });
    },
    [embeddingService.embeddingEnabled, embeddingService.embeddingProgress, t]
  );

  const handleReindex = useCallback(async () => {
    try {
      const result = await embeddingStore.reindexWorkspace(workspaceId);
      notify.success({
        title:
          t[
            'com.streamlain.settings.workspace.indexer-embedding.reindex.started'
          ](),
        message: t[
          'com.streamlain.settings.workspace.indexer-embedding.reindex.started-detail'
        ]({
          deleted: String(result.deletedChunks),
          queued: String(result.queuedDocs),
        }),
      });
      embeddingService.embeddingProgress.startEmbeddingProgressPolling();
    } catch (error) {
      notify.error({
        title:
          t[
            'com.streamlain.settings.workspace.indexer-embedding.reindex.failed'
          ](),
        message: UserFriendlyError.fromAny(error).message,
      });
    }
    // Started or failed: either way the dialog has done its part.
    return true;
  }, [embeddingStore, workspaceId, embeddingService.embeddingProgress, t]);

  const handleSelectDoc = useCallback(() => {
    if (isIgnoredDocsLoading) {
      return;
    }
    const initialIds = ignoredDocs.map((doc: { docId: string }) => doc.docId);
    workspaceDialogService.open(
      'doc-selector',
      {
        init: initialIds,
        title:
          t[
            'com.streamlain.settings.workspace.indexer-embedding.embedding.ignore-docs.picker-title'
          ](),
      },
      (selectedIds: string[] | undefined) => {
        if (selectedIds === undefined) {
          return;
        }
        track.$.settingsPanel.indexerEmbedding.addIgnoredDocs({
          type: 'Embedding',
          control: 'Additional docs',
          result: 'success',
        });
        const add = selectedIds.filter(
          (id: string) => !initialIds?.includes(id)
        );
        const remove = initialIds?.filter(
          (id: string) => !selectedIds.includes(id)
        );
        embeddingService
          .updateIgnoredDocs(add, remove ?? [])
          .catch((error: unknown) => {
            const err = UserFriendlyError.fromAny(error);
            notify.error({
              title:
                t[
                  'com.streamlain.settings.workspace.indexer-embedding.embedding.update-ignored-docs.error'
                ](),
              message: t[`error.${err.name}`](err.data),
            });
          });
      }
    );
  }, [
    ignoredDocs,
    isIgnoredDocsLoading,
    workspaceDialogService,
    embeddingService,
    t,
  ]);

  useEffect(() => {
    embeddingService.embeddingProgress.startEmbeddingProgressPolling();
    embeddingService.embeddingEnabled.getEnabled();
    embeddingService.ignoredDocs.getIgnoredDocs();
    embeddingService.embeddingProgress.getEmbeddingProgress();

    return () => {
      embeddingService.embeddingProgress.stopEmbeddingProgressPolling();
    };
  }, [
    embeddingService.embeddingProgress,
    embeddingService.embeddingEnabled,
    embeddingService.ignoredDocs,
  ]);

  const reindexTitle =
    t['com.streamlain.settings.workspace.indexer-embedding.reindex.title']();

  return (
    <>
      <SettingsRow
        label={t[
          'com.streamlain.settings.workspace.indexer-embedding.embedding.switch.title'
        ]()}
        description={t[
          'com.streamlain.settings.workspace.indexer-embedding.embedding.switch.description'
        ]()}
      >
        {({ labelId }) => (
          <Switch
            data-testid="workspace-embedding-setting-switch"
            aria-labelledby={labelId}
            checked={embeddingEnabled ?? false}
            onChange={handleEmbeddingToggle}
            disabled={isEnabledLoading}
          />
        )}
      </SettingsRow>
      <SettingsCollapsible
        label={t[
          'com.streamlain.settings.workspace.indexer-embedding.advanced'
        ]()}
        testId="embedding-advanced"
      >
        <SettingsSubRows>
          <EmbeddingProgress
            status={embeddingProgress}
            workspaceId={workspaceId}
          />
          <SettingsRow
            label={reindexTitle}
            description={t[
              'com.streamlain.settings.workspace.indexer-embedding.reindex.description'
            ]()}
          >
            <SettingsTypedConfirmButton
              label={reindexTitle}
              busyLabel={t[
                'com.streamlain.settings.workspace.indexer-embedding.reindex.busy'
              ]()}
              title={t[
                'com.streamlain.settings.workspace.indexer-embedding.reindex.confirm-title'
              ]()}
              description={t[
                'com.streamlain.settings.workspace.indexer-embedding.reindex.confirm-description'
              ]()}
              word={REINDEX_WORD}
              onConfirm={handleReindex}
              testId="workspace-embedding-reindex-button"
              inputTestId="workspace-embedding-reindex-confirm-input"
            />
          </SettingsRow>
          <SettingsRow
            label={t[
              'com.streamlain.settings.workspace.indexer-embedding.embedding.ignore-docs.title'
            ]()}
            description={t[
              'com.streamlain.settings.workspace.indexer-embedding.embedding.ignore-docs.description'
            ]()}
          >
            <Button
              data-testid="workspace-embedding-setting-ignore-docs-button"
              onClick={handleSelectDoc}
            >
              {t[
                'com.streamlain.settings.workspace.indexer-embedding.embedding.select-doc'
              ]()}
            </Button>
          </SettingsRow>
          {ignoredDocs.length > 0 ? (
            <IgnoredDocs
              ignoredDocs={ignoredDocs}
              isLoading={isIgnoredDocsLoading}
            />
          ) : null}
          {advancedExtra}
        </SettingsSubRows>
      </SettingsCollapsible>
    </>
  );
};

/** Whether the platform offers the intelligent search at all. */
export function useEmbeddingAvailable(): boolean {
  const serverConfig = useLiveData(useService(ServerService).server.config$);
  return Boolean(
    serverConfig?.features.includes(ServerFeature.CopilotEmbedding)
  );
}

/**
 * The intelligent search of the workspace: its switch and, under "Advanced",
 * the index behind it. Only for platforms where `useEmbeddingAvailable` holds.
 */
export const EmbeddingSettings = ({
  advancedExtra,
}: EmbeddingSettingsProps) => {
  const isLocal = useService(WorkspaceService).workspace.flavour === 'local';
  return isLocal ? (
    <EnableCloudPanel />
  ) : (
    <EmbeddingCloud advancedExtra={advancedExtra} />
  );
};
